import OrderListSkeleton from "components/Skeletons/OrderListSkeleton/OrderListSkeleton.tsx";
import styles from "containers/Orders/order-styles.module.css";
import { Box } from "@mui/material";
import NoOrdersErrorBanner from "../NoOrdersErrorBanner/NoOrderErrorBanner";
import {
	exportOrdersText,
	GeneratingPDFText,
	OrderLimitMessageBottom,
	PDFDownloadText,
	PDFErrorText
} from "constants/text";
import VirtualizedOrderList from "./VirtualizedOrderList";
import { sortBy } from "utils/order";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import useMyOrdersPDFGenerator from "hooks/useMyOrdersPDFGenerator";
import OrderSummaryAPIFailure from "components/OrderSummaryAPIFailure/OrderSummaryAPIFailure";
import ExportButtons from "../ExportFile/ExportButtons";
import useSnackbarEffect from "hooks/useSnackbarEffect";
import SearchBarFilters from "components/OrderSearch/SearchBarFilters";
import { FilterOpts } from "pages/OrderPages/OrderListPage/OrderListPage";
import OrderListErrorsWarning from "./OrderListErrorsWarning";
import { OrderListFiltersContainer, OrderListSkeletonListWrapper } from "./OrderList.styles";
import { StyledSearchOrderLimitHelperText } from "components/Form/HelperText/helperText.styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { models } from "types/api/viewModels.ts";
import { Dispatch, SetStateAction } from "react";
import { generateMyOrdersExcel } from "components/OrderList/helpers/MyOrdersExportsHelpers.ts";

const OrderList = ({
	filteredOrders,
	orders,
	orderStatus,
	isLoading,
	searchFilters,
	searchDates,
	totalStatuses,
	currentSearch,
	userDetails,
	filterOptions,
	setFilterOptions,
	showOrders,
	hasErrors,
	accounts
}: Props) => {
	const { pdfData, generateMyOrdersPDF } = useMyOrdersPDFGenerator(
		filteredOrders,
		searchFilters,
		searchDates,
		totalStatuses,
		currentSearch
	);
	const { snackbarOpen } = useSnackbarEffect(pdfData.loading);
	const largePDF = filteredOrders.length > 800;

	if (orderStatus !== 200) {
		return (
			<div className={styles.orderSummaryErrorContainer}>
				<OrderSummaryAPIFailure statusCode={orderStatus} />
			</div>
		);
	}

	if (isLoading) {
		return (
			<OrderListSkeletonListWrapper>
				{Array(3)
					.fill(1)
					.map((_value, index) => (
						<OrderListSkeleton key={index} />
					))}
			</OrderListSkeletonListWrapper>
		);
	}

	return (
		<div data-testid="order-list">
			<Box
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				height="2rem"
			>
				<div
					data-testid="order-list-order-count"
					className={styles.cardTotalOrders}
				>
					Showing {filteredOrders.length} results
				</div>
				{!isLoading && filteredOrders.length > 0 && (
					<ExportButtons
						largePDF={largePDF}
						buttonHeader={exportOrdersText}
						generatePDF={generateMyOrdersPDF}
						generateExcel={() => generateMyOrdersExcel(orders)}
					/>
				)}
			</Box>
			<Snackbar
				open={snackbarOpen}
				isLoading={pdfData.loading}
				loadingText={GeneratingPDFText}
				successText={PDFDownloadText}
				errorText={PDFErrorText}
				isError={Boolean(pdfData.error)}
			/>
			{showOrders && (
				<OrderListFiltersContainer>
					<SearchBarFilters
						orders={orders}
						accounts={accounts}
						userDetails={userDetails}
						filterOptions={filterOptions}
						setFilterOptions={setFilterOptions}
					/>
				</OrderListFiltersContainer>
			)}
			{hasErrors && <OrderListErrorsWarning />}
			{!isLoading && orderStatus !== undefined && orderStatus === 200 && filteredOrders.length === 0 && (
				<NoOrdersErrorBanner data-testid="order-list-no-order-banner" />
			)}
			{filteredOrders.length > 0 && (
				<VirtualizedOrderList items={sortBy(filteredOrders, filterOptions.sortedBy?.[0])} />
			)}
			<StyledSearchOrderLimitHelperText
				isLimitError
				hasBackground
			>
				<ErrorOutlineIcon aria-label="Warning" />
				<span>{OrderLimitMessageBottom}</span>
			</StyledSearchOrderLimitHelperText>
		</div>
	);
};

interface Props {
	filteredOrders: any;
	orders: any;
	orderStatus: any;
	isLoading: boolean;
	searchFilters: any;
	searchDates: any;
	totalStatuses: any;
	currentSearch: any;
	userDetails?: models["UserViewModel"];
	filterOptions: FilterOpts;
	setFilterOptions: Dispatch<SetStateAction<FilterOpts>>;
	showOrders: boolean;
	hasErrors: boolean;
	accounts?: models["CustomerAccountViewModel"][] | null;
}

export default OrderList;
